/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {QueryClient} from '@tanstack/react-query';
// import {persistQueryClient} from '@tanstack/react-query-persist-client';
// import {createAsyncStoragePersister} from '@tanstack/query-async-storage-persister';

import {deleteTokensFromCookies} from '#utils/cookies';
import {toastManager} from './toast-manager';
// import {getFromLS, removeFromLS, setToLS} from '../utils/localStorage';
// import {MINUTE} from '../constants';
// import {DEFAULT_DEBOUNCE_DELAY} from '../utils/constants';

// const localStorageAsync = {
//   getItem: async (key: string) => {
//     try {
//       const value = getFromLS(key);
//       return Promise.resolve(value);
//     } catch (error) {
//       return Promise.reject(error);
//     }
//   },

//   setItem: async (key: string, value: string) => {
//     try {
//       setToLS(key, value);
//       return Promise.resolve();
//     } catch (error) {
//       return Promise.reject(error);
//     }
//   },

//   removeItem: async (key: string) => {
//     try {
//       removeFromLS(key);
//       return Promise.resolve();
//     } catch (error) {
//       return Promise.reject(error);
//     }
//   }
// };

// export const persister = createAsyncStoragePersister({
//   storage: localStorageAsync,
//   throttleTime: DEFAULT_DEBOUNCE_DELAY
// });

// Create a client with optimized defaults for Next.js
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes by default
      staleTime: 5 * 60 * 1000,
      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
      // Retry failed requests 3 times
      retry: (failureCount, error: any) => {
        // Don't retry on authentication errors
        if (error?.message?.includes('Authentication failed')) {
          return false;
        }
        // Don't retry on 403 (Forbidden) or 404 (Not Found) errors
        // These are client errors that won't be resolved by retrying
        if (error?.status === 403 || error?.status === 404) {
          return false;
        }
        return failureCount < 3;
      },
      // Don't refetch on window focus (good for mobile)
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect by default
      refetchOnReconnect: false,
      // Don't refetch on mount if data is fresh
      refetchOnMount: false,
      // Don't refetch on interval by default
      refetchInterval: false,
      refetchIntervalInBackground: false,
      // Enable structural sharing - prevents unnecessary re-renders when data shape is the same
      structuralSharing: true,
      // Network mode - only run queries when online
      networkMode: 'online'
      // Enable query deduplication (prevents duplicate simultaneous requests)
      // This is enabled by default but explicitly set for clarity
    },
    mutations: {
      // Retry mutations once, but not for 403 or 404 errors
      retry: (failureCount, error: any) => {
        // Don't retry on 403 (Forbidden) or 404 (Not Found) errors
        // These are client errors that won't be resolved by retrying
        if (error?.status === 403 || error?.status === 404) {
          return false;
        }
        return failureCount < 1;
      },
      // Network mode for mutations
      networkMode: 'online'
    }
  }
});

// Helper function to clear tokens and logout user
function clearTokensAndLogout(reason: string) {
  // Show session expired toast
  toastManager.showSessionExpired();

  deleteTokensFromCookies();
  localStorage.clear();
  sessionStorage.clear();

  queryClient.invalidateQueries();
  queryClient.removeQueries();
  queryClient.clear();
  // persister.removeClient();

  // Redirect to login page with delay to allow user to see the toast
  if (typeof window !== 'undefined') {
    const currentPath = window.location.pathname;
    const locale = currentPath.match(/^\/([a-z]{2})/)?.[1] || 'en';

    setTimeout(() => {
      window.location.href = `/${locale}/sign-in`;
    }, 3000); // 3 second delay to allow user to read the toast
  }
}

// Global error handler for authentication failures
queryClient.setMutationDefaults(['auth'], {
  onError: (error: any) => {
    if (error?.message?.includes('Authentication failed')) {
      clearTokensAndLogout('Authentication failed in mutation');
    }
  }
});

// persistQueryClient({
//   // @ts-ignore
//   queryClient,
//   persister,
//   maxAge: MINUTE * 30
// });

// Note: Query-level error handling is done in the api-client.ts baseFetch function
// which automatically handles 401 errors and token refresh

// Query key factory for consistent key generation
export const queryKeys = {
  // Categories
  categories: {
    all: ['categories'] as const,
    lists: () => [...queryKeys.categories.all, 'list'] as const,
    list: (filters: Record<string, any>) =>
      [...queryKeys.categories.lists(), filters] as const,
    details: () => [...queryKeys.categories.all, 'detail'] as const,
    detail: (id: string | number) =>
      [...queryKeys.categories.details(), id] as const,
    b2c: () => [...queryKeys.categories.all, 'b2c'] as const,
    organisation: () => [...queryKeys.categories.all, 'organisation'] as const,
    organisationB2c: () =>
      [...queryKeys.categories.all, 'organisation-b2c'] as const,
    benefit: () => [...queryKeys.categories.all, 'benefit'] as const
  },

  // Benefits
  benefits: {
    all: ['benefits'] as const,
    lists: () => [...queryKeys.benefits.all, 'list'] as const,
    list: (filters: Record<string, any>) =>
      [...queryKeys.benefits.lists(), filters] as const,
    details: () => [...queryKeys.benefits.all, 'detail'] as const,
    detail: (id: string | number) =>
      [...queryKeys.benefits.details(), id] as const,
    popular: () => [...queryKeys.benefits.all, 'popular'] as const,
    organisation: () => [...queryKeys.benefits.all, 'organisation'] as const,
    merchant: () => [...queryKeys.benefits.all, 'merchant'] as const,
    user: () => [...queryKeys.benefits.all, 'user'] as const
  },

  // Organisation
  organisation: {
    all: ['organisation'] as const,
    lists: () => [...queryKeys.organisation.all, 'list'] as const,
    list: (filters: Record<string, any>) =>
      [...queryKeys.organisation.lists(), filters] as const,
    details: () => [...queryKeys.organisation.all, 'detail'] as const,
    detail: (id: string | number) =>
      [...queryKeys.organisation.details(), id] as const,
    webshop: (id: string | number) =>
      [...queryKeys.organisation.all, 'webshop', id] as const,
    suggested: () => [...queryKeys.organisation.all, 'suggested'] as const,
    global: () => [...queryKeys.organisation.all, 'global'] as const,
    feed: () => [...queryKeys.organisation.all, 'feed'] as const,
    subscriptions: () =>
      [...queryKeys.organisation.all, 'subscriptions'] as const,
    byCategory: (id: string | number) =>
      [...queryKeys.organisation.all, 'category', id] as const,
    allSubscriptions: () =>
      [...queryKeys.organisation.all, 'all-subscriptions'] as const,
    suggestions: () => [...queryKeys.organisation.all, 'suggestions'] as const
  },

  // Auth
  auth: {
    all: ['auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
    profile: () => [...queryKeys.auth.all, 'profile'] as const
  },

  // User
  user: {
    all: ['user'] as const,
    profile: () => [...queryKeys.user.all, 'profile'] as const,
    subscriptions: () => [...queryKeys.user.all, 'subscriptions'] as const,
    statistics: () => [...queryKeys.user.all, 'statistics'] as const,
    publicWebshop: () => [...queryKeys.user.all, 'publicWebshop'] as const,
    walkthrough: () => [...queryKeys.user.all, 'walkthrough'] as const
  },

  // Merchant
  merchant: {
    all: ['merchant'] as const,
    lists: () => [...queryKeys.merchant.all, 'list'] as const,
    list: (filters: Record<string, any>) =>
      [...queryKeys.merchant.lists(), filters] as const,
    details: () => [...queryKeys.merchant.all, 'detail'] as const,
    detail: (id: string | number) =>
      [...queryKeys.merchant.details(), id] as const,
    organisations: () => [...queryKeys.merchant.all, 'organisations'] as const,
    organisationRequests: () =>
      [...queryKeys.merchant.all, 'organisation-requests'] as const,
    suggestions: () => [...queryKeys.merchant.all, 'suggestions'] as const
  },

  // Subscriptions
  subscriptions: {
    all: ['subscriptions'] as const,
    lists: () => [...queryKeys.subscriptions.all, 'list'] as const,
    list: (filters: Record<string, any>) =>
      [...queryKeys.subscriptions.lists(), filters] as const,
    details: () => [...queryKeys.subscriptions.all, 'detail'] as const,
    detail: (id: string | number) =>
      [...queryKeys.subscriptions.details(), id] as const
  },

  // Notifications
  notifications: {
    all: ['notifications'] as const,
    lists: () => [...queryKeys.notifications.all, 'list'] as const,
    list: (filters: Record<string, any>) =>
      [...queryKeys.notifications.lists(), filters] as const,
    details: () => [...queryKeys.notifications.all, 'detail'] as const,
    detail: (id: string | number) =>
      [...queryKeys.notifications.details(), id] as const,
    unreadExists: () =>
      [...queryKeys.notifications.all, 'unread-exists'] as const
  },

  // Settings
  settings: {
    all: ['settings'] as const,
    app: () => [...queryKeys.settings.all, 'app'] as const,
    user: () => [...queryKeys.settings.all, 'user'] as const,
    merchant: () => [...queryKeys.settings.all, 'merchant'] as const,
    organisation: () => [...queryKeys.settings.all, 'organisation'] as const,
    webshop: () => [...queryKeys.settings.all, 'webshop'] as const
  },

  // 1881
  api1881: {
    all: ['api1881'] as const,
    phone: (phone: string) =>
      [...queryKeys.api1881.all, 'phone', phone] as const,
    organisation: (number: string) =>
      [...queryKeys.api1881.all, 'organisation', number] as const
  },

  // Receipts
  receipts: {
    all: ['receipts'] as const,
    linkedCards: () => [...queryKeys.receipts.all, 'linked-cards'] as const,
    session: () => [...queryKeys.receipts.all, 'session'] as const
  },

  // Cashback
  cashback: {
    all: ['cashback'] as const,
    general: () => [...queryKeys.cashback.all, 'general'] as const,
    fundsFlow: (filters: Record<string, any>) =>
      [...queryKeys.cashback.all, 'funds-flow', filters] as const,
    overview: (filters: Record<string, any>) =>
      [...queryKeys.cashback.all, 'overview', filters] as const,
    withdrawalHistory: (filters: Record<string, any>) =>
      [...queryKeys.cashback.all, 'withdrawals', filters] as const,
    invoiceHistory: (filters: Record<string, any>) =>
      [...queryKeys.cashback.all, 'invoices', filters] as const,
    bankAccount: (filters: Record<string, any>) =>
      [...queryKeys.cashback.all, 'bank-accounts', filters] as const
  }
} as const;
